import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Layer } from './Layer';
import { Inline } from './layout';
import styles from './tooltip.css';
import { TextTiny } from './typography';

export function Tooltip({
  label,
  shortcut,
  anchorRef,
  placement = 'vertical',
}: {
  label: string;
  shortcut?: string;
  anchorRef: React.RefObject<HTMLElement>;
  placement?: 'horizontal' | 'vertical';
}) {
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [_visible, setVisible] = React.useState(false);
  const visible = _visible && label !== '';

  React.useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const onMouseEnter = () => setVisible(true);
    const onMouseLeave = () => setVisible(false);
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
    return () => {
      el.removeEventListener('mouseenter', onMouseEnter);
      el.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [anchorRef]);
  usePinElementPlacement(
    visible,
    anchorRef,
    tooltipRef,
    placement === 'vertical'
      ? placeTooltipVertically
      : placeTooltipHorizontally,
  );

  return (
    <CSSTransition
      nodeRef={tooltipRef}
      in={visible}
      timeout={ANIMATION_TIMEOUT_MS}
      classNames={styles}
      unmountOnExit
    >
      <Layer>
        <div className={styles.tooltip} ref={tooltipRef}>
          <Inline spacing={8}>
            <TextTiny>{label}</TextTiny>
            {shortcut && <TextTiny color="grey">{shortcut}</TextTiny>}
          </Inline>
        </div>
      </Layer>
    </CSSTransition>
  );
}

const ANIMATION_TIMEOUT_MS = { enter: 600, exit: 100 };

const placeTooltipVertically: PlacementFunction = (
  anchor,
  content,
  contentEl,
) => {
  const openAbove = anchor.top - content.height > 32;
  const pad = 4;
  const top = openAbove
    ? anchor.top - content.height - pad
    : anchor.bottom + pad;
  const width = Math.min(window.innerWidth - pad * 4, content.width);
  const left = Math.max(
    pad,
    Math.min(
      anchor.left + anchor.width / 2 - width / 2,
      window.innerWidth - width - pad,
    ),
  );
  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';
};
export const placeTooltipHorizontally: PlacementFunction = (
  anchor,
  content,
  contentEl,
) => {
  const pad = 4;
  const top = Math.max(
    pad,
    Math.min(
      anchor.top + anchor.height / 2 - content.height / 2,
      window.innerHeight - content.height - pad,
    ),
  );
  const openRight = anchor.right + content.width > window.innerWidth - 32;
  const left = openRight
    ? anchor.left - content.width - pad
    : anchor.right + pad;
  contentEl.style.top = top + 'px';
  contentEl.style.left = left + 'px';
};

export type PlacementFunction = (
  anchor: DOMRect,
  content: { width: number; height: number },
  contentEl: HTMLElement,
) => void;

export function usePinElementPlacement(
  visible: boolean,
  anchorRef: React.RefObject<HTMLElement>,
  contentRef: React.RefObject<HTMLElement>,
  place: PlacementFunction,
) {
  React.useEffect(() => {
    if (!visible) return;
    let disposed = false;

    function frame() {
      if (disposed) return;
      window.requestAnimationFrame(frame);

      const anchor = anchorRef.current;
      const content = contentRef.current;
      if (!anchor || !content) return;

      const anchorRect = anchor.getBoundingClientRect();
      if (anchorRect.width === 0 || anchorRect.height === 0) {
        // Commonly this is the anchor unmounting, so we stay in the same
        // place.  Hopefully the consumer is animating out...
        // But it could also be during mount there's an initial 0x0 rect.
        // So keep ticking :)
        return;
      }
      const contentRect = content.getBoundingClientRect();
      place(anchorRect, contentRect, content);
    }

    frame();
    return () => {
      disposed = true;
    };
  }, [visible, anchorRef, contentRef, place]);
}

export function usePinPointPlacement(
  point: { x: number; y: number } | undefined,
  contentRef: React.RefObject<HTMLElement>,
  place: PlacementFunction,
) {
  React.useEffect(() => {
    if (!point) return;
    let disposed = false;
    function frame() {
      const content = contentRef.current;
      if (disposed || !point || !content) return;
      const contentRect = content.getBoundingClientRect();
      place(new DOMRect(point.x, point.y, 0, 0), contentRect, content);
      window.requestAnimationFrame(frame);
    }
    frame();
    return () => {
      disposed = true;
    };
  }, [contentRef, place, point]);
}
